import React, { useEffect, useMemo, useState } from 'react';

import { Calendar, DateObject } from 'react-multi-date-picker';
import { ButtonClassic } from '@/components/buttons/ButtonClassic';
import { Counter } from '@/components/counter/Counter';
import { Pill } from '@/components/filters/Pill';
import { CloseIcon } from '@/components/icons';
import { Modal } from '@/components/modal';

import { findAvailableTours } from '@/actions/find-available-tours';
import { fetchAvailability } from '@/actions/fetch-availability';
import { months as allMonths } from '@/config/constants';
import CalendarPlaceholder from './CalendarPlaceholder';
import { YearPicker } from './YearPicker';
import { TMonth } from '@/types';
import './calendar.css';
import { TAvailability, TAvailableMonths, useReservation } from './hooks/useReservation';
import { Visitors } from './Visitors';
import { YearMonthPicker } from './MonthPicker';
import { AvailabilityCalendar } from './AvailabilityCalendar';

const weekDays = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

const today = new Date();

interface ITourReservationModalProps {
  isOpen: boolean;
  tourId: string;
  yearMonth: {
    [key: string]: Array<number>;
  };
  handleClose: () => void;
}

export const TourReservationModal = ({ isOpen, tourId, yearMonth, handleClose }: ITourReservationModalProps) => {
  const { availability, year, selectedMonth, ...reservation } = useReservation({ tourId, yearMonth, handleClose });

  return (
    <Modal
      isOpen={isOpen}
      closeModal={handleClose}
      displayCloseButton={true}
      className="flex flex-col h-screen lg:h-auto w-full max-w-[915px] mx-auto bg-gray-900 relative"
    >
      <div className="flex flex-row justify-between lg:justify-end p-4 lg:pb-0">
        <div className="block lg:hidden">Change</div>
        <div className="p-2.5 lg:p-0 cursor-pointer" onClick={handleClose}>
          <div className="p-[5px]">
            <CloseIcon />
          </div>
        </div>
      </div>

      <div className="hidden lg:block">
        <DesktopModal reservation={reservation} availability={availability} year={year} selectedMonth={selectedMonth} />
      </div>

      <div className="block lg:hidden h-[calc(100vh-72px)]">
        <MobileModal reservation={reservation} availability={availability} year={year} selectedMonth={selectedMonth} />
      </div>
    </Modal>
  );
};

interface IDesktopProps {
  availability: TAvailability;
  year: number;
  selectedMonth: number | null;
  reservation: {
    [key: string]: any;
    months: Array<TAvailableMonths>;
    adultCount: number;
    incrementAdults: () => void;
    decrementAdults: () => void;
    childrenCount: number;
    incrementChildren: () => void;
    decrementChildren: () => void;
    incrementYear: () => void;
    decrementYear: () => void;
    handleChangeMonth: (monthValue: number) => void;
    calendarValue: DateObject | null;
    handleChangeCalendarValue: (value: DateObject) => void;
  };
}

const DesktopModal = ({ reservation, availability, year, selectedMonth }: IDesktopProps) => (
  <div className="flex flex-col gap-8 px-20 pt-8 pb-12 bg-gray-900">
    <h2 className="cursor-default text-xl lg:text-2xl">Find available tours</h2>
    <div className="flex flex-col gap-8">
      <div className="flex flex-col lg:flex-row justify-between gap-[20px]">
        {/* LEFT SIDE */}
        <div className="w-full flex flex-col gap-10">
          <Visitors reservation={reservation} />
          <YearMonthPicker selectedMonth={selectedMonth} year={year} reservation={reservation} />
        </div>
        {/* VERTICAL DIVIDER */}
        <div className="block border-l border-l-gray-800"></div> {/* hidden lg-block */}
        {/* RIGHT SIDE */}
        <AvailabilityCalendar
          selectedMonth={selectedMonth}
          year={year}
          availability={availability}
          reservation={reservation}
        />
      </div>
    </div>
    <div className="flex flex-row justify-end items-center">
      <ButtonClassic
        handleClick={() => reservation.handleSubmit(false)}
        className={`text-gray-500 bg-gray-800 ${reservation.isDaySelected ? 'text-gray-900  bg-primary-500 hover:bg-primary-400' : ''}`}
      >
        See available tours
      </ButtonClassic>
    </div>
  </div>
);

// element po element
// na klik podesi search params, ali ne zatvaraj modal
// // ako vec ima search params, prvo prikazi vreme - tab 2
// // ako nema, prvo prikazi kalendar - tab 1
// u useEffect stavi da odredjuje koji ce tab biti
//
// na dugme nazad ukloni search params
// na dugme clear ukloni search params i resetuj state

const MobileModal = ({ reservation, availability, year, selectedMonth }: IDesktopProps) => (
  <>
    <div className="flex flex-col gap-8 px-8 pb-4 bg-gray-900 overflow-auto h-[calc(100vh-72px-72px)] scrollbar-none">
      {reservation.tab === 1 && (
        <>
          <h2 className="cursor-default text-xl lg:text-2xl">Find available tours</h2>
          <div className="flex flex-col gap-8">
            <div className="flex flex-col lg:flex-row justify-between gap-[20px]">
              <div className="w-full flex flex-col gap-10">
                <section className="w-full flex flex-col gap-6">
                  <h3 className="text-md font-semibold cursor-default">Add visitors</h3>
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-row justify-between items-center">
                      <span className="font-inter text-md font-light cursor-default">Adults</span>
                      <Counter
                        count={reservation.adultCount}
                        increment={reservation.incrementAdults}
                        decrement={reservation.decrementAdults}
                      />
                    </div>
                    <div className="flex flex-row justify-between items-center">
                      <span className="font-inter text-md font-light cursor-default">Children</span>
                      <Counter
                        count={reservation.childrenCount}
                        increment={reservation.incrementChildren}
                        decrement={reservation.decrementChildren}
                      />
                    </div>
                  </div>
                </section>

                <section className="flex flex-col gap-6">
                  <div className="flex flex-row justify-between items-center w-auto">
                    <h3 className="text-md font-semibold">What month works for you</h3>
                    <YearPicker
                      year={year}
                      increment={reservation.incrementYear}
                      decrement={reservation.decrementYear}
                    />
                  </div>
                  <div className="flex flex-row flex-wrap gap-2">
                    {reservation.months.map((month) => (
                      <Pill
                        key={month.value}
                        label={month.label}
                        value={month.value}
                        isDisabled={!month.isAvailable}
                        className=""
                        handleClick={() => reservation.handleChangeMonth(month.value)}
                        isSelected={selectedMonth === month.value}
                      />
                    ))}
                  </div>
                </section>
              </div>
              {/* HORIZONTAL DIVIDER - Mobile */}
              <div className="block lg:hidden border-b border-b-gray-800"></div> {/* hidden lg-block */}
              {/* RIGHT SIDE */}
              <div className="w-full">
                <div className="flex grow flex-col h-full justify-between items-start">
                  <h3 className="text-md font-semibold cursor-default">Select a date</h3>
                  {selectedMonth != null && availability[year][selectedMonth] ? (
                    <Calendar
                      hideMonth
                      hideYear
                      value={reservation.calendarValue}
                      onChange={reservation.handleChangeCalendarValue}
                      buttons={false}
                      weekDays={weekDays}
                      minDate={today}
                      shadow={false}
                      mapDays={(props) => {
                        const zeroIndexed = props.date.day - 1;
                        const isAvailable = availability[year][selectedMonth][zeroIndexed];
                        if (!isAvailable) {
                          return {
                            disabled: true,
                          };
                        }
                      }}
                      className="calendar-dark"
                    />
                  ) : (
                    <CalendarPlaceholder />
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
    <div className="flex flex-row justify-between items-center border-t border-gray-900 py-3 px-8 h-[72px]">
      <ButtonClassic handleClick={reservation.resetForm}>Clear</ButtonClassic>
      <ButtonClassic
        handleClick={() => reservation.handleSubmit(true)}
        className={`text-gray-500 bg-gray-800 ${reservation.isDaySelected ? 'text-gray-900  bg-primary-500 hover:bg-primary-400' : ''}`}
      >
        See available tours
      </ButtonClassic>
    </div>
  </>
);
